<template>
  <div
    @click="togglePlay()"
    class="interaction-video-wrapper full-width"
  >
    <v-progress-linear class='buffer-progress branding-primary' :indeterminate="buffering" v-show='buffering && desiredState !== "paused"'></v-progress-linear>
    <video class="interaction-video" :id="'interaction-video-' + interaction.id"  webkit-playsinline></video>
    <v-fade-transition>
      <v-btn
        @click.stop="toggleDefaultHlsCaptions"
        v-if="hasDefaultHlsCaptions"
        transition="fade-transition"
        class="secondary-stream-default-hls-caption-button"
        icon
        small
        color="grey lighten-2"
      >
        <v-icon
          small
          :color="areDefaultHlsCaptionsEnabled ? 'primary' : null"
        >
          {{ areDefaultHlsCaptionsEnabled ? 'closed_caption' : 'closed_caption_off' }}
        </v-icon>
      </v-btn>
    </v-fade-transition>
  </div>
</template>

<script>
/* global _ */
/* global moment */
import {
  mapState,
  mapActions,
  mapGetters
} from 'vuex';
import _ from 'lodash';
import VideoPlayer from '@/components/mixins/VideoPlayer';

export default {
  name: 'InteractionVideo',
  props: [
    'interaction',
    'interactionMeta',
    'visible'
  ],
  mixins: [
    VideoPlayer
  ],
  data() {
    return {
      animationFrameId: null,
      buffering: false,
      player: null,
      seekThreshold: 0.5,
      syncThreshold: 0.10,
      thresholdExcess: 0,
      videoSrc: null,
      videoType: null,
      hasDefaultHlsCaptions: false,
      areDefaultHlsCaptionsEnabled: false,
    }
  },
  computed: {
    ...mapState('player', [
      'pqOrigin',
      'proquestVideoUrl',
      'youtubePlayerConfiguration',
    ]),
    ...mapGetters('player', [
      'bulb',
      'currentSegmentSeek',
      'desiredState',
      'displayTime',
      'playerState',
      'speed',
      'shouldUseYoutubeEducationPlayer',
    ]),
  },
  methods: {
    ...mapActions('player', [
      'togglePlay'
    ]),
    expectedPlayerTime() {
      return this.displayTime - this.interaction.displayInTime;
    },
    loadInteractionVideo() {
      this.videoSrc = this.formatUrl(this.interaction.interactionVideo.video.videoStreams[0]);
      this.videoType = this.formatType(this.interaction.interactionVideo.video.videoStreams[0]);

      this.player = videojs(`interaction-video-${this.interaction.id}`, {
        autoplay: false,
        captions: false,
        controls: false,
        controlBar:false,
        muted: true,
        playsinline: true,
        posterImage: false,
        preload: 'auto',
        techOrder: _.compact([
          'html5',
          this.bulb?.teachers?.[0]?.school?.permissionKeys?.includes('enableIframeVideojsTech') ? 'Iframe' : null,
          'youtube', 
          'panopto',
          'Vimeo',
        ]),
        textTrackSettings: false,
        panopto: {
          panoptoBaseUrl: _.get(this, 'bulb.teachers[0].school.panopto', null),
          pauseImmediately: true,
          service: 'PlayerInteractionVideo',
        },
        youtube: {
          ...this.shouldUseYoutubeEducationPlayer && {
            embedConfig: {
              enc: this.youtubePlayerConfiguration,
            },
            host: 'https://www.youtubeeducation.com',
          },
          'customVars': {
            'playsinline': '1',
            'enablejsapi':'1',
            'stretch':'16:9',
            'modestbranding': '1',
            'iv_load_policy': '3',
            'cc_load_policy': 3,
            'rel': 0
          }
        },
        Vimeo: {
          autopause: false,
        },
        Iframe: {
          debug: false,
          pqOrigin: this.pqOrigin,
          proquestVideoUrl: this.proquestVideoUrl,
        },
      });
      this.player.src({
        src: this.videoSrc,
        type: this.videoType,
      });

      // [ ] Refactor videojs player events in order to remove them correctly later.
      this.player.ready(() => {
        this.player.removeChild('loadingSpinner')
      });
      this.player.on('play', () => {
        this.buffering = false;
      });
      this.player.on('pause', () => {
        this.buffering = false;
      });
      this.player.on('seek', () => {
        this.buffering = false;
      });
      this.player.on('seeked', () => {
        this.buffering = false;
        this.seeking = false;
        // this.syncSecondaryStream();
      });
      this.player.on('seeking', (e) => {
        this.buffering = true; // turn on buffering progress bar above video instance
        this.seeking = true;
      });
      this.player.on('waiting', (e) => {
        this.buffering = true; // turn on buffering progress bar above video instance
      });
      this.player.on('loadedmetadata', error => {
        const tracks = this?.player?.textTracks?.() || [];
        for (let i = 0; i < tracks.length; i++) {
          const track = tracks[i];
          if (track.default) {
            track.mode = 'disabled';
            this.hasDefaultHlsCaptions = true;
          }
        }
      });
      this.player.on('error', error => {
        console.log('error');
      });
      // end of videojs player events
    },
    play() {
      this.player.play().catch(err => {
        this.syncSecondaryStream();
      });
      this.startTimeUpdateLoop();
    },
    pause() {
      this.player.pause();
      this.stopTimeUpdateLoop();
    },
    seek(playerTime) {
      if (!this.player || this.seeking) return;
      this.seeking = true;
      this.player.currentTime(playerTime);
    },
    startTimeUpdateLoop() {
      if (typeof this.animationFrameId !== 'number' && this.interactionMeta.inDisplayRange) {
        this.animationFrameId = requestAnimationFrame(this.timeUpdateLoop);
      } else {
        this.stopTimeUpdateLoop();
      }
    },
    timeUpdateLoop() {
      if ((!this.player)) {// || this.player.paused())) {
        return this.stopTimeUpdateLoop();
      }
      this.syncSecondaryStream();
      this.animationFrameId = requestAnimationFrame(this.timeUpdateLoop);
    },
    stopTimeUpdateLoop() {
      this.animationFrameId = cancelAnimationFrame(this.animationFrameId);
    },
    syncPlayState() {
      if (this.interactionMeta.inDisplayRange) {
        // interaction visible and in display range
        if (this.playerState === 'play' && this.player.paused()) {
          // syncronize play
          this.play();
        }
        else if (this.playerState === 'paused' && !this.player.paused()) {
          // syncronize pause
          this.pause();
        }
      }
      else {
        // reset this.pause();secondary stream
        this.pause();
        this.seek(0);
        this.stopTimeUpdateLoop();
      }
    },
    syncSecondaryStream() {
      if (!this.player || !this.interactionMeta.inDisplayRange) return;
      if (this.syncPlayState()) return;
      let playerTime = this.player.currentTime();
      let secondary_stream_is_behind = (playerTime - this.expectedPlayerTime()) < 0;
      let diff = Math.abs(this.expectedPlayerTime() - playerTime);
      // The secondary stream is within the acceptable bounds if its time is within 0.1 seconds of the primary stream.
      // console.log(playerTime, secondary_stream_is_behind, diff)
      if (diff >= this.seekThreshold) {
        // console.log('\tseek ss to reach primary stream')
        // Videos are more than 0.5s apart. Secondary stream should seek to match the primary stream because just increasing/decreasing the playback rate will not be enough.
        this.thresholdExcess = 0;
        // console.log(this.seeking, this.buffering)
        this.seek(this.expectedPlayerTime());
      } else if (diff <= this.syncThreshold && this.player.playbackRate() !== this.speed) {
        // console.log('\t ss in acceptable range')
        // Secondary stream is within the acceptable bounds. Reset the thrshold_excess and return the playback speed to whatever it originally was
        this.player.playbackRate(this.speed);
        this.thresholdExcess = 0;
      } else if (diff > this.syncThreshold) {
        // console.log('\tss needs to sync')
        // The secondary stream is outside of the acceptable bounds. Add 1 to the thresholdExcess counter. If the counter reaches 5 (meaning there have been 5 timeUpdateLoop cycles in a row where the secondary stream is not within the acceptable bounds) then speed up/slow down the secondary stream until it's back within that acceptable range.
        this.thresholdExcess++;
        if (this.thresholdExcess >= 5) {
          this.player.playbackRate(this.speed * (secondary_stream_is_behind ? 1.5 : .5));
          this.thresholdExcess = 0;
        }
      } else {
        if (this.buffering) this.buffering = false
      }
    },
    destroyStream: async function() {
      return new Promise( (resolve, reject) => {
        this.stopTimeUpdateLoop();
        // [ ] refactor the videojs player events in order to disable them.
        if (document.getElementById(`interaction-video-${this.interaction.id}`)) videojs(document.getElementById(`interaction-video-${this.interaction.id}`)).dispose();
        this.$nextTick(() => {
          if (videojs.getPlayers() && Object.keys(videojs.getPlayers()).length && videojs.getPlayers()[`interaction-video-${this.interaction.id}`]) {
            delete videojs.getPlayers()[`interaction-video-${this.interaction.id}`];
          }
          this.player = null;
          return resolve(true);
        });
      });
    },
    toggleDefaultHlsCaptions() {
      const tracks = this?.player?.textTracks?.() || [];
      for (let i = 0; i < tracks.length; i++) {
        const track = tracks[i];
        if (track.default) track.mode = this.areDefaultHlsCaptionsEnabled ? 'disabled' : 'showing';
      }
      this.areDefaultHlsCaptionsEnabled = !this.areDefaultHlsCaptionsEnabled;
      if (!this.player) return;
      this.player.textTrackDisplay.updateDisplay();
    },
  },
  watch: {
    'currentSegmentSeek'() {
      if (this.currentSegmentSeek) {
        this.syncSecondaryStream();
      }
    },
    'interactionMeta.inDisplayRange'(n, o) {
      if (n) {
        this.startTimeUpdateLoop();
        if (this.player?.techName_ === 'Panopto') {
           this.player?.tech_?.panoptoPlayer?.loadVideo();
           this.player?.tech_?.panoptoPlayer?.muteVideo();
        }
      }
      else {
        this.pause();
        this.stopTimeUpdateLoop();
      }
    },
    'playerState'() {
      if (this.playerState === 'play') this.play();
      else this.pause();
    },
    'speed'() {
      this.player.playbackRate(parseFloat(this.speed));
    }
  },
  mounted() {
    this.loadInteractionVideo();
  },
  beforeDestroy() {
    if (!this.player) return;
    this.destroyStream();
  },
};
</script>

<style>

  .secondaryStream .question-app {
    height: 100%;
    overflow: hidden;
  }
  .secondaryStream .interaction-video {
    height: 101%;
    width: 100%;
  }

  .interaction-video iframe {
    pointer-events: none;
  }

  .secondary-stream-default-hls-caption-button {
    position: absolute !important;
    top: 50%;
    transform: translateY(-50%);
    right: 0;
    z-index: 99999;
  }

</style>
